import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  AfterViewInit,
  Renderer2,
  HostListener,
  input,
  output,
} from '@angular/core';
import { DecorateUntilDestroy, returnToAngularZone, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { Store } from '@skyeng/libs/shared/store';
import { NEVER, Observable, fromEvent, merge } from 'rxjs';
import { tap, map, filter, switchMap } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';

const storeInitialState = { isDisabled: false };

@DecorateUntilDestroy()
@Directive({
  selector: '[maDropFilesZone]',
  standalone: false,
})
export class DropFilesZoneDirective implements AfterViewInit, OnDestroy {
  readonly cssClassOnWindowEnter = input('-show');
  readonly cssClassOnElemEnter = input('-active');
  readonly actionSelector = input<string>(undefined, { alias: 'maDropFilesZoneActionSelector' });
  readonly isDisabled = input(false, { alias: 'maDropFilesZoneDisabled' });

  readonly filesDropped = output<FileList | File[]>();

  private store = new Store(storeInitialState);

  private outerDragEnterCounter = 0;
  private innerDragEnterCounter = 0;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private renderer: Renderer2,
  ) {
    toObservable(this.isDisabled)
      .pipe(takeUntilDestroyed(this))
      .subscribe(isDisabled => {
        this.store.updateFields({ isDisabled });
      });
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.store
        .select('isDisabled')
        .pipe(
          switchMap(isDisabled => {
            if (isDisabled) {
              return NEVER;
            }

            return this.init$();
          }),
          takeUntilDestroyed(this),
        )
        .subscribe();
    });
  }

  ngOnDestroy(): void {}

  private init$(): Observable<unknown> {
    const windowDragEnter$ = fromEvent<DragEvent>(window, 'dragenter').pipe(tap(event => this.onDragEnter(event)));

    const windowDragOver$ = fromEvent<DragEvent>(window, 'dragover').pipe(tap(event => event.preventDefault()));

    const windowDragLeave$ = fromEvent<DragEvent>(window, 'dragleave').pipe(tap(event => this.onDragLeave(event)));

    const windowDrop$ = fromEvent<DragEvent>(window, 'drop').pipe(tap(event => this.onDrop(event)));

    const targetDragEnter$ = fromEvent<DragEvent>(this.elRef.nativeElement, 'dragenter').pipe(
      tap(() => this.onTargetDragEnter()),
    );

    const targetDragLeave$ = fromEvent<DragEvent>(this.elRef.nativeElement, 'dragleave').pipe(
      tap(event => this.onTargetDragLeave(event)),
    );

    const targetDrop$ = fromEvent<DragEvent>(this.elRef.nativeElement, 'drop').pipe(
      tap(event => this.onTargetDrop(event)),
      returnToAngularZone(this.ngZone),
    );

    const selector$ = this.subscribeSelector$();

    return merge(
      windowDragEnter$,
      windowDragOver$,
      windowDragLeave$,
      windowDrop$,
      targetDragEnter$,
      targetDragLeave$,
      targetDrop$,
      selector$,
    );
  }

  private onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.outerDragEnterCounter++;
    this.elRef.nativeElement.classList.add(this.cssClassOnWindowEnter());
  }

  private onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.outerDragEnterCounter--;
    if (this.outerDragEnterCounter === 0) {
      this.elRef.nativeElement.classList.remove(this.cssClassOnWindowEnter());
    }
  }

  private onTargetDragEnter(): void {
    this.innerDragEnterCounter++;
    this.elRef.nativeElement.classList.add(this.cssClassOnElemEnter());
  }

  private onTargetDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.innerDragEnterCounter--;
    if (this.innerDragEnterCounter === 0) {
      this.elRef.nativeElement.classList.remove(this.cssClassOnElemEnter());
    }
  }

  private onDrop(event: DragEvent): void {
    this.dropHandler(event);
  }

  private onTargetDrop(event: DragEvent): void {
    this.dropHandler(event);
    const files = event.dataTransfer?.files;
    if ((files?.length || -1) > 0) {
      this.filesDropped.emit(files);
    }
  }

  private dropHandler(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.outerDragEnterCounter = 0;
    this.innerDragEnterCounter = 0;
    this.elRef.nativeElement.classList.remove(this.cssClassOnElemEnter());
    this.elRef.nativeElement.classList.remove(this.cssClassOnWindowEnter());
  }

  private subscribeSelector$(): Observable<unknown> {
    const actionSelector = this.actionSelector();
    if (!actionSelector) {
      return NEVER;
    }
    const actionElement = this.elRef.nativeElement.querySelector(actionSelector);
    if (!actionElement) {
      return NEVER;
    }
    const input = document.createElement('input');
    input.setAttribute('multiple', '');
    input.setAttribute('type', 'file');
    this.renderer.setStyle(input, 'display', 'none');
    this.elRef.nativeElement.append(input);

    const click$ = fromEvent(actionElement, 'click').pipe(tap(() => input.click()));

    const change$ = fromEvent(input, 'change').pipe(
      map(() => input.files),
      filter(files => !!files && files.length > 0),
      tap(files => files && this.filesDropped.emit(files)),
    );

    return merge(click$, change$);
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const { clipboardData } = event;
    if (this.isDisabled() || !clipboardData || !clipboardData.items) {
      return;
    }
    const image = this.getImage(clipboardData);
    if (!image) {
      return;
    }
    event.preventDefault();
    const file = image.getAsFile();
    if (!file) {
      return;
    }
    this.filesDropped.emit([file]);
  }

  private getImage(data: DataTransfer): DataTransferItem | null {
    for (let i = 0; i < data.items.length; i++) {
      const item = data.items[i];
      if (item.type.startsWith('image')) {
        return item;
      }
    }
    return null;
  }
}
